// 阅读引擎核心功能实现

import type {
  BookInfo,
  Chapter,
  ChapterContent,
  ReadingProgress
} from '@/types/reading';

import { BookSearchManager } from './bookSearchManager';
import type { StorageManager, LocalStorageManager } from './storage';

export interface ReadingSession {
  bookId: string;
  chapterId: string;
  startTime: Date;
  lastActiveTime: Date;
  readingTime: number; // 累计阅读时间（毫秒）
  scrollPosition: number;
  isActive: boolean;
}

export interface ChapterNavigation {
  currentChapter: Chapter;
  previousChapter?: Chapter;
  nextChapter?: Chapter;
  chapterIndex: number;
  totalChapters: number;
}

export class ReadingEngine {
  private searchManager: BookSearchManager;
  private storage: StorageManager;
  private localStorage: LocalStorageManager;
  private currentSession?: ReadingSession;
  private progressSaveInterval?: NodeJS.Timeout;
  private readonly PROGRESS_SAVE_INTERVAL = 10000; // 10秒保存一次进度

  constructor(
    searchManager: BookSearchManager,
    storage: StorageManager,
    localStorage: LocalStorageManager
  ) {
    this.searchManager = searchManager;
    this.storage = storage;
    this.localStorage = localStorage;
  }

  // ============================================================================
  // 章节内容获取和显示
  // ============================================================================

  async openBook(bookId: string, chapterId?: string): Promise<{
    book: BookInfo;
    chapter: Chapter;
    content: ChapterContent;
    navigation: ChapterNavigation;
    progress: ReadingProgress;
  } | null> {
    try {
      // 1. 获取图书信息
      const book = await this.searchManager.getBookDetail(bookId);
      if (!book || book.chapters.length === 0) {
        return null;
      }

      // 2. 确定要打开的章节
      let targetChapter: Chapter;
      
      if (chapterId) {
        const chapter = book.chapters.find(ch => ch.id === chapterId);
        if (!chapter) {
          return null;
        }
        targetChapter = chapter;
      } else {
        // 获取上次阅读进度
        const progress = await this.getReadingProgress(bookId);
        if (progress && progress.currentChapterId) {
          const chapter = book.chapters.find(ch => ch.id === progress.currentChapterId);
          targetChapter = chapter || book.chapters[0];
        } else {
          targetChapter = book.chapters[0];
        }
      }

      // 3. 获取章节内容
      const content = await this.searchManager.getChapterContent(bookId, targetChapter.id);
      if (!content) {
        return null;
      }

      // 4. 构建导航信息
      const navigation = this.buildChapterNavigation(book.chapters, targetChapter);

      // 5. 获取或创建阅读进度
      let progress = await this.getReadingProgress(bookId);
      if (!progress) {
        progress = this.createInitialProgress(bookId, targetChapter);
      }

      // 6. 开始阅读会话
      await this.startReadingSession(bookId, targetChapter.id);

      return {
        book,
        chapter: targetChapter,
        content,
        navigation,
        progress
      };

    } catch (error) {
      console.error('Open book failed:', error);
      return null;
    }
  }

  async getChapterContent(bookId: string, chapterId: string): Promise<ChapterContent | null> {
    try {
      return await this.searchManager.getChapterContent(bookId, chapterId);
    } catch (error) {
      console.error('Get chapter content failed:', error);
      return null;
    }
  }

  // ============================================================================
  // 阅读进度自动保存
  // ============================================================================

  private async startReadingSession(bookId: string, chapterId: string): Promise<void> {
    try {
      // 结束之前的会话
      if (this.currentSession) {
        await this.endReadingSession();
      }

      // 创建新会话
      this.currentSession = {
        bookId,
        chapterId,
        startTime: new Date(),
        lastActiveTime: new Date(),
        readingTime: 0,
        scrollPosition: 0,
        isActive: true
      };

      // 开始自动保存进度
      this.startProgressAutoSave();

    } catch (error) {
      console.error('Start reading session failed:', error);
    }
  }

  private startProgressAutoSave(): void {
    if (this.progressSaveInterval) {
      clearInterval(this.progressSaveInterval);
    }

    this.progressSaveInterval = setInterval(async () => {
      if (this.currentSession && this.currentSession.isActive) {
        await this.saveCurrentProgress();
        this.updateReadingTime();
      }
    }, this.PROGRESS_SAVE_INTERVAL);
  }

  private async saveCurrentProgress(): Promise<void> {
    if (!this.currentSession) {
      return;
    }

    try {
      const book = await this.searchManager.getBookDetail(this.currentSession.bookId);
      if (!book) {
        return;
      }

      const chapterIndex = book.chapters.findIndex(ch => ch.id === this.currentSession!.chapterId);
      const progress = (chapterIndex + 1) / book.chapters.length;

      const readingProgress: ReadingProgress = {
        bookId: this.currentSession.bookId,
        chapterId: this.currentSession.chapterId,
        position: this.currentSession.scrollPosition,
        percentage: progress * 100,
        lastReadTime: new Date(),
        totalReadTime: this.currentSession.readingTime,
        currentChapterId: this.currentSession.chapterId,
        currentChapterIndex: chapterIndex,
        progress: progress,
        totalReadingTime: this.currentSession.readingTime
      };

      // 保存到存储
      await this.storage.saveUserData('progress', readingProgress, this.currentSession.bookId);
      
      // 快速访问保存到localStorage
      this.localStorage.saveReadingProgress(readingProgress);

    } catch (error) {
      console.error('Save current progress failed:', error);
    }
  }

  private updateReadingTime(): void {
    if (!this.currentSession || !this.currentSession.isActive) {
      return;
    }

    const now = new Date();
    const timeDiff = now.getTime() - this.currentSession.lastActiveTime.getTime();
    
    // 只有在合理的时间间隔内才更新（避免页面长时间未活动的情况）
    if (timeDiff < 60000) { // 1分钟内
      this.currentSession.readingTime += timeDiff;
    }
    
    this.currentSession.lastActiveTime = now;
  }

  async updateScrollPosition(position: number): Promise<void> {
    if (this.currentSession) {
      this.currentSession.scrollPosition = position;
      this.currentSession.lastActiveTime = new Date();
    }
  }

  async pauseReading(): Promise<void> {
    if (this.currentSession) {
      this.currentSession.isActive = false;
      await this.saveCurrentProgress();
    }
  }

  async resumeReading(): Promise<void> {
    if (this.currentSession) {
      this.currentSession.isActive = true;
      this.currentSession.lastActiveTime = new Date();
    }
  }

  async endReadingSession(): Promise<void> {
    if (this.currentSession) {
      this.currentSession.isActive = false;
      await this.saveCurrentProgress();
      
      if (this.progressSaveInterval) {
        clearInterval(this.progressSaveInterval);
        this.progressSaveInterval = undefined;
      }
      
      this.currentSession = undefined;
    }
  }

  // ============================================================================
  // 章节导航和翻页功能
  // ============================================================================

  async navigateToChapter(bookId: string, chapterId: string): Promise<{
    chapter: Chapter;
    content: ChapterContent;
    navigation: ChapterNavigation;
  } | null> {
    try {
      const book = await this.searchManager.getBookDetail(bookId);
      if (!book) {
        return null;
      }

      const chapter = book.chapters.find(ch => ch.id === chapterId);
      if (!chapter) {
        return null;
      }

      const content = await this.getChapterContent(bookId, chapterId);
      if (!content) {
        return null;
      }

      const navigation = this.buildChapterNavigation(book.chapters, chapter);

      // 更新当前会话
      if (this.currentSession && this.currentSession.bookId === bookId) {
        this.currentSession.chapterId = chapterId;
        this.currentSession.scrollPosition = 0;
        this.currentSession.lastActiveTime = new Date();
      }

      return {
        chapter,
        content,
        navigation
      };

    } catch (error) {
      console.error('Navigate to chapter failed:', error);
      return null;
    }
  }

  async navigateToNextChapter(bookId: string): Promise<{
    chapter: Chapter;
    content: ChapterContent;
    navigation: ChapterNavigation;
  } | null> {
    try {
      if (!this.currentSession || this.currentSession.bookId !== bookId) {
        return null;
      }

      const book = await this.searchManager.getBookDetail(bookId);
      if (!book) {
        return null;
      }

      const currentIndex = book.chapters.findIndex(ch => ch.id === this.currentSession!.chapterId);
      if (currentIndex === -1 || currentIndex >= book.chapters.length - 1) {
        return null; // 已是最后一章
      }

      const nextChapter = book.chapters[currentIndex + 1];
      return await this.navigateToChapter(bookId, nextChapter.id);

    } catch (error) {
      console.error('Navigate to next chapter failed:', error);
      return null;
    }
  }

  async navigateToPreviousChapter(bookId: string): Promise<{
    chapter: Chapter;
    content: ChapterContent;
    navigation: ChapterNavigation;
  } | null> {
    try {
      if (!this.currentSession || this.currentSession.bookId !== bookId) {
        return null;
      }

      const book = await this.searchManager.getBookDetail(bookId);
      if (!book) {
        return null;
      }

      const currentIndex = book.chapters.findIndex(ch => ch.id === this.currentSession!.chapterId);
      if (currentIndex <= 0) {
        return null; // 已是第一章
      }

      const previousChapter = book.chapters[currentIndex - 1];
      return await this.navigateToChapter(bookId, previousChapter.id);

    } catch (error) {
      console.error('Navigate to previous chapter failed:', error);
      return null;
    }
  }

  private buildChapterNavigation(chapters: Chapter[], currentChapter: Chapter): ChapterNavigation {
    const currentIndex = chapters.findIndex(ch => ch.id === currentChapter.id);
    
    return {
      currentChapter,
      previousChapter: currentIndex > 0 ? chapters[currentIndex - 1] : undefined,
      nextChapter: currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : undefined,
      chapterIndex: currentIndex,
      totalChapters: chapters.length
    };
  }

  async getChapterList(bookId: string): Promise<Chapter[]> {
    try {
      const book = await this.searchManager.getBookDetail(bookId);
      return book?.chapters || [];
    } catch (error) {
      console.error('Get chapter list failed:', error);
      return [];
    }
  }

  // ============================================================================
  // 阅读进度管理
  // ============================================================================

  async getReadingProgress(bookId: string): Promise<ReadingProgress | null> {
    try {
      // 先从localStorage快速获取
      const localProgress = this.localStorage.getReadingProgress(bookId);
      if (localProgress) {
        return localProgress;
      }

      // 从存储获取
      const progress = await this.storage.getUserData('progress', bookId);
      return progress || null;

    } catch (error) {
      console.error('Get reading progress failed:', error);
      return null;
    }
  }

  private createInitialProgress(bookId: string, chapter: Chapter): ReadingProgress {
    return {
      bookId,
      chapterId: chapter.id,
      position: 0,
      percentage: 0,
      lastReadTime: new Date(),
      totalReadTime: 0,
      currentChapterId: chapter.id,
      currentChapterIndex: 0,
      progress: 0,
      totalReadingTime: 0
    };
  }

  async getAllReadingProgress(): Promise<ReadingProgress[]> {
    try {
      // 这里需要获取所有图书的进度，但当前存储接口不支持
      // 可以通过获取所有用户数据然后筛选progress类型来实现
      return [];
    } catch (error) {
      console.error('Get all reading progress failed:', error);
      return [];
    }
  }

  async deleteReadingProgress(bookId: string): Promise<boolean> {
    try {
      await this.storage.deleteUserData('progress', bookId);
      return true;
    } catch (error) {
      console.error('Delete reading progress failed:', error);
      return false;
    }
  }

  // ============================================================================
  // 阅读统计
  // ============================================================================

  async getReadingStats(): Promise<{
    totalReadingTime: number; // 总阅读时间（分钟）
    booksRead: number; // 已读图书数量
    chaptersRead: number; // 已读章节数量
    averageReadingSpeed: number; // 平均阅读速度（字/分钟）
    readingStreak: number; // 连续阅读天数
    todayReadingTime: number; // 今日阅读时间（分钟）
    weeklyReadingTime: number; // 本周阅读时间（分钟）
    monthlyReadingTime: number; // 本月阅读时间（分钟）
  }> {
    try {
      // 这里需要实现统计逻辑，当前返回默认值
      return {
        totalReadingTime: 0,
        booksRead: 0,
        chaptersRead: 0,
        averageReadingSpeed: 0,
        readingStreak: 0,
        todayReadingTime: 0,
        weeklyReadingTime: 0,
        monthlyReadingTime: 0
      };
    } catch (error) {
      console.error('Get reading stats failed:', error);
      return {
        totalReadingTime: 0,
        booksRead: 0,
        chaptersRead: 0,
        averageReadingSpeed: 0,
        readingStreak: 0,
        todayReadingTime: 0,
        weeklyReadingTime: 0,
        monthlyReadingTime: 0
      };
    }
  }

  // ============================================================================
  // 会话管理
  // ============================================================================

  getCurrentSession(): ReadingSession | undefined {
    return this.currentSession;
  }

  isReading(): boolean {
    return this.currentSession?.isActive || false;
  }

  getCurrentBook(): string | undefined {
    return this.currentSession?.bookId;
  }

  getCurrentChapter(): string | undefined {
    return this.currentSession?.chapterId;
  }

  // ============================================================================
  // 清理和销毁
  // ============================================================================

  async destroy(): Promise<void> {
    await this.endReadingSession();
  }
}